import { createSlice } from "@reduxjs/toolkit";
import {
  getMailConversationsThunk,
  getMailConversationByIdThunk,
  updateMailConversationStatusThunk,
  getMailStatsThunk,
} from "../actions/mail.action";
import {
  IMailConversation,
  IMailPagination,
  IMailStats,
} from "../../models/mail.model";

export interface MailState {
  conversations: {
    messages: IMailConversation[];
    pagination: IMailPagination;
    count: number;
  };
  currentConversation: IMailConversation | null;
  stats: IMailStats | null;
  statsLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: MailState = {
  conversations: {
    messages: [],
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: 10,
    },
    count: 0,
  },
  currentConversation: null,
  stats: null,
  statsLoading: false,
  loading: false,
  error: null,
};

const mailSlice = createSlice({
  name: "mail",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMailConversationsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMailConversationsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.data;
      })
      .addCase(getMailConversationsThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
    builder
      .addCase(getMailConversationByIdThunk.pending, (state) => {
        // Only show loading spinner on initial load, not during polling
        if (!state.currentConversation) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getMailConversationByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConversation = action.payload.data;
      })
      .addCase(getMailConversationByIdThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
    builder
      .addCase(updateMailConversationStatusThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(updateMailConversationStatusThunk.fulfilled, (state, action) => {
        if (state.currentConversation?._id === action.payload.data._id) {
          state.currentConversation = action.payload.data;
        }
        // Also update in conversations list if it exists
        const conversationIndex = state.conversations.messages.findIndex(
          (conv) => conv._id === action.payload.data._id
        );
        if (conversationIndex !== -1) {
          state.conversations.messages[conversationIndex] = action.payload.data;
        }
      })
      .addCase(updateMailConversationStatusThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    builder
      .addCase(getMailStatsThunk.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(getMailStatsThunk.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload.data;
      })
      .addCase(getMailStatsThunk.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentConversation } = mailSlice.actions;
export default mailSlice.reducer;
